<template>
  <Teleport to="body">
    <CpAddonPackageTour
      v-if="tourAddonCode"
      :key="tourMountKey"
      :addon-code="tourAddonCode"
      :auto-start="true"
      @tour-ended="onTourEnded"
    />

    <!-- Prompt shown after a tour ends when more tourable packages are queued -->
    <Transition name="cp-next-tour-prompt">
      <div
        v-if="nextTourPromptCode"
        class="fixed inset-0 z-[145] flex items-end justify-center p-4 sm:items-center sm:p-6"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="'cp-next-tour-title'"
      >
        <div
          class="absolute inset-0 bg-zinc-950/60 backdrop-blur-sm"
          @click="declineNextTour"
        />
        <div
          class="cp-next-tour-card relative w-full max-w-sm overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-2xl dark:border-gray-700/60 dark:bg-gray-900"
          @click.stop
        >
          <div class="px-6 pt-6 pb-2">
            <div class="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-600/30" aria-hidden="true">
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
              </svg>
            </div>
            <p id="cp-next-tour-title" class="text-sm font-semibold text-gray-900 dark:text-white">
              {{ t('cpPackages.tour.nextPrompt.title') }}
            </p>
            <p class="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
              {{ t('cpPackages.tour.nextPrompt.message', { name: nextTourPackageName }) }}
            </p>
            <p v-if="tourQueue.length > 1" class="mt-1 text-xs text-gray-400 dark:text-gray-500">
              {{ t('cpPackages.tour.nextPrompt.remaining', { count: tourQueue.length - 1 }) }}
            </p>
          </div>
          <div class="flex items-center justify-end gap-2 px-6 py-4">
            <BaseButton variant="secondary" size="sm" @click="declineNextTour">
              {{ t('cpPackages.tour.nextPrompt.skip') }}
            </BaseButton>
            <BaseButton variant="primary" size="sm" @click="acceptNextTour">
              {{ t('cpPackages.tour.nextPrompt.start') }}
            </BaseButton>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="cp-addon-celebration-overlay">
      <div
        v-if="visible"
        class="cp-celebration-backdrop fixed inset-0 z-[140] flex items-center justify-center p-4 sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-addon-celebration-title"
      >
        <button
          type="button"
          class="absolute inset-0 cursor-default bg-zinc-950/75 backdrop-blur-md dark:bg-black/80"
          :aria-label="t('common.close')"
          @click="dismiss"
        />
        <div
          class="celebration-shell relative w-full max-w-2xl overflow-hidden rounded-3xl border border-emerald-200/60 bg-white text-left shadow-[0_0_0_1px_rgba(16,185,129,0.08),0_25px_50px_-12px_rgba(5,150,105,0.35)] dark:border-emerald-800/50 dark:bg-gray-950 dark:shadow-[0_0_0_1px_rgba(52,211,153,0.12),0_25px_60px_-12px_rgba(0,0,0,0.85)]"
          @click.stop
        >
          <div class="celebration-shell-glow pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-emerald-400/25 blur-3xl dark:bg-emerald-500/15" aria-hidden="true" />
          <div class="celebration-shell-glow pointer-events-none absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-teal-400/20 blur-3xl dark:bg-teal-500/10" aria-hidden="true" />

          <div class="relative border-b border-emerald-100/80 bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/50 px-6 py-8 text-center dark:border-emerald-900/40 dark:from-emerald-950/40 dark:via-gray-950 dark:to-teal-950/20 sm:px-10 sm:py-10">
            <div
              class="cp-addon-celebration-icon mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-600/30 ring-4 ring-white/80 dark:ring-emerald-950/50"
              aria-hidden="true"
            >
              <svg
                v-if="heroGlyphPath"
                class="h-11 w-11"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" :d="heroGlyphPath" />
              </svg>
              <svg v-else class="h-11 w-11" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.847a4.5 4.5 0 003.09 3.09L15.75 12l-2.847.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
                />
              </svg>
            </div>
            <p class="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700/90 dark:text-emerald-400/90">
              {{ t('cpPackages.celebration.subtitle') }}
            </p>
            <h2
              id="cp-addon-celebration-title"
              class="mt-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl"
            >
              {{ t('cpPackages.celebration.title') }}
            </h2>
          </div>

          <div class="relative max-h-[min(52vh,28rem)] space-y-4 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8">
            <article
              v-for="(code, index) in displayCodes"
              :key="code"
              class="celebration-package-card group relative overflow-hidden rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-white via-emerald-50/40 to-teal-50/30 p-1 shadow-md shadow-emerald-900/5 ring-1 ring-emerald-500/10 dark:border-emerald-800/60 dark:from-gray-900 dark:via-emerald-950/25 dark:to-teal-950/20 dark:ring-emerald-400/10"
              :style="{ animationDelay: `${120 + index * 95}ms` }"
            >
              <div
                class="pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-emerald-400 via-teal-500 to-cyan-500 opacity-90 dark:from-emerald-500 dark:via-teal-400 dark:to-cyan-400"
                aria-hidden="true"
              />
              <div class="relative flex items-center gap-4 rounded-xl bg-white/80 p-4 pl-5 backdrop-blur-sm dark:bg-gray-900/75 sm:gap-5 sm:p-5 sm:pl-6">
                <div
                  class="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-inner shadow-black/10 sm:h-16 sm:w-16"
                  aria-hidden="true"
                >
                  <svg
                    v-if="celebrationGlyphPathByCode[code]"
                    class="h-8 w-8 opacity-95"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" :d="celebrationGlyphPathByCode[code]" />
                  </svg>
                  <svg v-else class="h-8 w-8 opacity-95" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                    />
                  </svg>
                </div>
                <div class="min-w-0 flex-1">
                  <span
                    class="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-800 ring-1 ring-inset ring-emerald-600/20 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-400/25"
                  >
                    {{ t('cpPackages.celebration.unlockedBadge') }}
                  </span>
                  <h3 class="mt-2 text-lg font-bold leading-snug text-gray-900 dark:text-white sm:text-xl">
                    {{ displayNameForCode(code) }}
                  </h3>
                  <p class="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                    {{ descriptionForCode(code) }}
                  </p>
                </div>
              </div>
            </article>
          </div>

          <div class="relative border-t border-emerald-100/90 bg-gradient-to-t from-emerald-50/30 to-transparent px-6 py-6 dark:border-emerald-900/40 dark:from-emerald-950/20 sm:px-10">
            <p class="text-center text-sm leading-relaxed text-gray-600 dark:text-gray-400">
              {{ t('cpPackages.celebration.hint') }}
            </p>
            <div class="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <BaseButton variant="primary" size="lg" class="w-full min-w-[12rem] shadow-lg shadow-emerald-600/25 sm:w-auto" @click="dismiss">
                {{ t('cpPackages.celebration.cta') }}
              </BaseButton>
              <BaseButton
                v-if="hasTourForAnyCode"
                variant="secondary"
                size="lg"
                class="w-full min-w-[12rem] sm:w-auto"
                @click="startTourAndDismiss"
              >
                {{ t('cpPackages.celebration.startTour') }}
              </BaseButton>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useTenantBrandingStore } from '@/stores/tenant-branding'
import BaseButton from '@/components/base/BaseButton.vue'
import CpAddonPackageTour from '@/components/layout/CpAddonPackageTour.vue'
import { getCpAddonCelebrationGlyphPath } from '@/utils/cp-addon-celebration-icons'

/** Add-on codes that have a tour defined in CpAddonPackageTour. */
const TOUR_SUPPORTED_CODES = new Set([
  'seating_pro',
  'gift_cards',
  'surveys',
  'memberships',
  'waitlist_automation',
  'integrations_plus',
  'sponsor_placements',
  'corporate_billing',
  'social',
  'ticket_buddy_connect',
])

const { t, te } = useI18n()
const brandingStore = useTenantBrandingStore()
const { pendingCpAddonCelebration } = storeToRefs(brandingStore)

const visible = computed(() => pendingCpAddonCelebration.value.length > 0)
const displayCodes = computed(() => [...pendingCpAddonCelebration.value].sort())

/** Currently running tour code. */
const tourAddonCode = ref<string | null>(null)
/**
 * Incremented every time launchTour() is called so the :key on CpAddonPackageTour
 * always changes, forcing a fresh mount even when the same code is launched twice.
 */
const tourMountKey = ref(0)

/**
 * Remaining tourable codes after the current tour finishes.
 * Populated when the user starts a tour from the overlay.
 */
const tourQueue = ref<string[]>([])

/**
 * Code waiting for the "start next tour?" prompt.
 * Shown after a tour ends if the queue is non-empty.
 */
const nextTourPromptCode = ref<string | null>(null)

/** Single unlocked add-on with a mapped glyph: reuse it in the hero; otherwise sparkles. */
const heroGlyphPath = computed((): string | null => {
  const codes = displayCodes.value
  if (codes.length !== 1) return null
  return getCpAddonCelebrationGlyphPath(codes[0]!)
})

/** Whether any of the pending codes has a tour available. */
const hasTourForAnyCode = computed(() =>
  displayCodes.value.some((c) => TOUR_SUPPORTED_CODES.has(c)),
)

/** Human-readable name for the package waiting in the next-tour prompt. */
const nextTourPackageName = computed(() => {
  if (!nextTourPromptCode.value) return ''
  return displayNameForCode(nextTourPromptCode.value)
})

/** SVG path `d` per add-on code for list rows (avoids repeated lookups in template). */
const celebrationGlyphPathByCode = computed((): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const c of displayCodes.value) {
    const d = getCpAddonCelebrationGlyphPath(c)
    if (d) out[c] = d
  }
  return out
})

function displayNameForCode(code: string): string {
  const key = `cpPackages.celebration.itemNames.${code}`
  return te(key) ? t(key) : code.replace(/_/g, ' ')
}

function descriptionForCode(code: string): string {
  const key = `cpPackages.celebration.itemDescriptions.${code}`
  return te(key) ? t(key) : t('cpPackages.celebration.itemDescriptionFallback')
}

function dismiss(): void {
  brandingStore.dismissCpAddonCelebration()
}

function launchTour(code: string): void {
  // Increment key first so Vue always remounts CpAddonPackageTour, even for repeated calls with the same code.
  tourMountKey.value += 1
  tourAddonCode.value = code
}

function startTourAndDismiss(): void {
  const tourableCodes = displayCodes.value.filter((c) => TOUR_SUPPORTED_CODES.has(c))
  if (tourableCodes.length === 0) return

  brandingStore.dismissCpAddonCelebration()

  // Build the queue: all tourable codes except the first one.
  tourQueue.value = tourableCodes.slice(1)
  launchTour(tourableCodes[0]!)
}

function onTourEnded(): void {
  tourAddonCode.value = null

  const next = tourQueue.value[0] ?? null
  if (next) {
    // Show the "start next tour?" prompt with a short delay so driver.js cleanup settles.
    window.setTimeout(() => {
      nextTourPromptCode.value = next
    }, 350)
  }
}

function acceptNextTour(): void {
  const code = nextTourPromptCode.value
  if (!code) return
  nextTourPromptCode.value = null
  tourQueue.value = tourQueue.value.slice(1)
  launchTour(code)
}

function declineNextTour(): void {
  nextTourPromptCode.value = null
  tourQueue.value = []
}
</script>

<style scoped>
.cp-celebration-backdrop.cp-addon-celebration-overlay-enter-from,
.cp-celebration-backdrop.cp-addon-celebration-overlay-leave-to {
  opacity: 0;
}

.cp-celebration-backdrop.cp-addon-celebration-overlay-enter-active,
.cp-celebration-backdrop.cp-addon-celebration-overlay-leave-active {
  transition: opacity 0.3s ease;
}

.cp-addon-celebration-overlay-enter-active .celebration-shell {
  transition:
    transform 0.45s cubic-bezier(0.34, 1.2, 0.64, 1),
    opacity 0.35s ease;
}

.cp-addon-celebration-overlay-enter-from .celebration-shell {
  transform: scale(0.96) translateY(12px);
  opacity: 0;
}

.cp-addon-celebration-overlay-enter-active .cp-addon-celebration-icon {
  transition:
    transform 0.55s cubic-bezier(0.34, 1.56, 0.64, 1),
    opacity 0.4s ease;
}

.cp-addon-celebration-overlay-enter-from .cp-addon-celebration-icon {
  transform: scale(0.6) rotate(-8deg);
  opacity: 0;
}

.celebration-package-card {
  opacity: 0;
  animation: celebration-package-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

@keyframes celebration-package-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.celebration-package-card:hover {
  border-color: rgba(16, 185, 129, 0.45);
  box-shadow:
    0 12px 40px -12px rgba(5, 150, 105, 0.2),
    0 0 0 1px rgba(16, 185, 129, 0.12);
}

/* Next-tour prompt transitions */
.cp-next-tour-prompt-enter-active,
.cp-next-tour-prompt-leave-active {
  transition: opacity 0.2s ease;
}

.cp-next-tour-prompt-enter-from,
.cp-next-tour-prompt-leave-to {
  opacity: 0;
}

.cp-next-tour-prompt-enter-active .cp-next-tour-card {
  transition: transform 0.3s cubic-bezier(0.34, 1.2, 0.64, 1), opacity 0.2s ease;
}

.cp-next-tour-prompt-enter-from .cp-next-tour-card {
  transform: translateY(16px) scale(0.97);
  opacity: 0;
}
</style>
